import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useProject } from '../../utils/ProjectContext';


import {
    Modal,
    Box,
    Typography,
    CircularProgress,
    Grid,
    Paper,
    IconButton,
    Tabs,
    Tab,
    FormGroup,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import '../../css/ReportsPage.css';
import CloseIcon from '@mui/icons-material/Close';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90vw',
    maxWidth: 1200,
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    overflow: 'auto',
};

const SessionDetailsModal = ({ open, onClose, sessionId }) => {
    const { VISION_URL } = useProject();
    const { projectId } = useParams();
    const [sessionData, setSessionData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [visibleMetrics, setVisibleMetrics] = useState({
        leftEye: true,
        rightEye: true,
        blinkLeft: true,
        blinkRight: true,
        mouthRatio: true,
        faceTilt: true
    });

    useEffect(() => {
        console.log('SessionDetails effect:', {
            open,
            sessionId,
            projectId
        });
        
        const fetchSessionData = async () => {
            if (!sessionId) return;

            setLoading(true);
            setError(null);

            try {
                console.log('Fetching session details:', {
                    url: `${VISION_URL}/api/analytics/sessions/${sessionId}`,
                    sessionId,
                    projectId
                });
                const response = await fetch(`${VISION_URL}/api/analytics/sessions/${sessionId}`);
                const responseText = await response.text();
                console.log('Response:', {
                    status: response.status,
                    text: responseText,
                    headers: Object.fromEntries([...response.headers])
                });
                if (!response.ok) {
                    console.error('Session details error:', {
                        status: response.status,
                        statusText: response.statusText,
                        sessionId: sessionId,
                        projectId: projectId,
                        responseText: responseText
                    });
                    throw new Error(`Failed to fetch session data: ${responseText}`);
                }
                const data = JSON.parse(responseText);
                console.log('Session details response:', {
                    metadata: data.metadata,
                    timeSeriesData: data.timeSeriesData,
                    stats: data.stats,
                    summaries: data.summaries,
                    fullData: data
                });
                setSessionData(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (open && sessionId) {
            fetchSessionData();
        }
    }, [open, sessionId]);

    const formatTimestamp = (timestamp) => {
        const seconds = Math.floor(timestamp);
        return `${seconds}s`;
    };

    const prepareChartData = (timeSeriesData) => {
        if (!timeSeriesData) return [];

        return timeSeriesData.timestamps.map((timestamp, index) => ({
            timestamp,
            leftEye: timeSeriesData.attention.leftEye[index],
            rightEye: timeSeriesData.attention.rightEye[index],
            mouthRatio: timeSeriesData.attention.mouthRatio[index],
            faceTilt: timeSeriesData.attention.faceTilt[index],
            blinkLeft: timeSeriesData.attention.blinkLeft[index] ? 1 : 0,
            blinkRight: timeSeriesData.attention.blinkRight[index] ? 1 : 0,
        }));
    };

    if (!open) return null;

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="session-details-modal"
        >
            <Box sx={modalStyle}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5" component="h2">
                        Session Analysis
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>

                {loading && (
                    <Box display="flex" justifyContent="center" my={4}>
                        <CircularProgress />
                    </Box>
                )}

                {error && (
                    <Typography color="error" my={2}>
                        Error: {error}
                    </Typography>
                )}

                {sessionData && (
                    <>
                        <Box sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider', 
                            mb: 3,
                            '& .MuiTabs-root': {
                                minHeight: '42px',
                            },
                            '& .MuiTab-root': {
                                fontSize: '0.82em',
                                fontWeight: 600,
                                textTransform: 'none',
                                minHeight: '42px',
                                color: '#4a5568',
                                '&.Mui-selected': {
                                    color: '#3b82f6',
                                }
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: '#3b82f6',
                                height: '2px'
                            }
                        }}>
                            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
                                <Tab label="Attention Metrics" />
                                <Tab label="Head Movement" />
                                <Tab label="Summary" />
                            </Tabs>
                        </Box>

                        {activeTab === 0 && (
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 3, borderRadius: '0.75rem', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                            <Typography variant="h6" sx={{ fontSize: '0.9em', fontWeight: 600, color: '#2d3748' }}>
                                                Eye Activity
                                            </Typography>
                                            <FormGroup row>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={visibleMetrics.leftEye}
                                                            onChange={(e) => setVisibleMetrics(prev => ({
                                                                ...prev,
                                                                leftEye: e.target.checked
                                                            }))}
                                                            size="small"
                                                            sx={{ color: '#3b82f6', '&.Mui-checked': { color: '#3b82f6' } }}
                                                        />
                                                    }
                                                    label={<Typography sx={{ fontSize: '0.75em' }}>Left Eye</Typography>}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={visibleMetrics.rightEye}
                                                            onChange={(e) => setVisibleMetrics(prev => ({
                                                                ...prev,
                                                                rightEye: e.target.checked
                                                            }))}
                                                            size="small"
                                                            sx={{ color: '#10b981', '&.Mui-checked': { color: '#10b981' } }}
                                                        />
                                                    }
                                                    label={<Typography sx={{ fontSize: '0.75em' }}>Right Eye</Typography>}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={visibleMetrics.blinkLeft}
                                                            onChange={(e) => setVisibleMetrics(prev => ({
                                                                ...prev,
                                                                blinkLeft: e.target.checked
                                                            }))}
                                                            size="small"
                                                            sx={{ color: '#f59e0b', '&.Mui-checked': { color: '#f59e0b' } }}
                                                        />
                                                    }
                                                    label={<Typography sx={{ fontSize: '0.75em' }}>Left Blink</Typography>}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={visibleMetrics.blinkRight}
                                                            onChange={(e) => setVisibleMetrics(prev => ({
                                                                ...prev,
                                                                blinkRight: e.target.checked
                                                            }))}
                                                            size="small"
                                                            sx={{ color: '#ef4444', '&.Mui-checked': { color: '#ef4444' } }}
                                                        />
                                                    }
                                                    label={<Typography sx={{ fontSize: '0.75em' }}>Right Blink</Typography>}
                                                />
                                            </FormGroup>
                                        </Box>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <LineChart
                                                data={prepareChartData(sessionData.timeSeriesData)}
                                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                                <XAxis
                                                    dataKey="timestamp"
                                                    tickFormatter={formatTimestamp}
                                                    stroke="#718096"
                                                    fontSize={11}
                                                />
                                                <YAxis stroke="#718096" fontSize={11} />
                                                <Tooltip
                                                    labelFormatter={formatTimestamp}
                                                    contentStyle={{
                                                        backgroundColor: 'white',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: '0.375rem',
                                                        fontSize: '0.75em'
                                                    }}
                                                />
                                                <Legend 
                                                    wrapperStyle={{
                                                        fontSize: '0.75em',
                                                        color: '#4a5568'
                                                    }}
                                                />
                                                {visibleMetrics.leftEye && (
                                                    <Line
                                                        type="monotone"
                                                        dataKey="leftEye"
                                                        stroke="#3b82f6"
                                                        name="Left Eye"
                                                        strokeWidth={2}
                                                    />
                                                )}
                                                {visibleMetrics.rightEye && (
                                                    <Line
                                                        type="monotone"
                                                        dataKey="rightEye"
                                                        stroke="#10b981"
                                                        name="Right Eye"
                                                        strokeWidth={2}
                                                    />
                                                )}
                                                {visibleMetrics.blinkLeft && (
                                                    <Line
                                                        type="step"
                                                        dataKey="blinkLeft"
                                                        stroke="#f59e0b"
                                                        name="Left Blink"
                                                        strokeWidth={1.5}
                                                    />
                                                )}
                                                {visibleMetrics.blinkRight && (
                                                    <Line
                                                        type="step"
                                                        dataKey="blinkRight"
                                                        stroke="#ef4444"
                                                        name="Right Blink"
                                                        strokeWidth={1.5}
                                                    />
                                                )}
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12}>
                                    <Paper sx={{ p: 3, borderRadius: '0.75rem', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                            <Typography variant="h6" sx={{ fontSize: '0.9em', fontWeight: 600, color: '#2d3748' }}>
                                                Mouth Movement
                                            </Typography>
                                            <FormGroup row>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={visibleMetrics.mouthRatio}
                                                            onChange={(e) => setVisibleMetrics(prev => ({
                                                                ...prev,
                                                                mouthRatio: e.target.checked
                                                            }))}
                                                            size="small"
                                                            sx={{ color: '#3b82f6', '&.Mui-checked': { color: '#3b82f6' } }}
                                                        />
                                                    }
                                                    label={<Typography sx={{ fontSize: '0.75em' }}>Mouth Movement</Typography>}
                                                />
                                            </FormGroup>
                                        </Box>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <LineChart
                                                data={prepareChartData(sessionData.timeSeriesData)}
                                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                                <XAxis
                                                    dataKey="timestamp"
                                                    tickFormatter={formatTimestamp}
                                                    stroke="#718096"
                                                    fontSize={11}
                                                />
                                                <YAxis stroke="#718096" fontSize={11} />
                                                <Tooltip
                                                    labelFormatter={formatTimestamp}
                                                    contentStyle={{
                                                        backgroundColor: 'white',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: '0.375rem',
                                                        fontSize: '0.75em'
                                                    }}
                                                />
                                                <Legend 
                                                    wrapperStyle={{
                                                        fontSize: '0.75em',
                                                        color: '#4a5568'
                                                    }}
                                                />
                                                {visibleMetrics.mouthRatio && (
                                                    <Line
                                                        type="monotone"
                                                        dataKey="mouthRatio"
                                                        stroke="#3b82f6"
                                                        name="Mouth Movement"
                                                        strokeWidth={2}
                                                    />
                                                )}
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </Paper>
                                </Grid>
                            </Grid>
                        )}

                        {activeTab === 1 && (
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 3, borderRadius: '0.75rem', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                            <Typography variant="h6" sx={{ fontSize: '0.9em', fontWeight: 600, color: '#2d3748' }}>
                                                Head Orientation
                                            </Typography>
                                            <FormGroup row>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={visibleMetrics.faceTilt}
                                                            onChange={(e) => setVisibleMetrics(prev => ({
                                                                ...prev,
                                                                faceTilt: e.target.checked
                                                            }))}
                                                            size="small"
                                                            sx={{ color: '#3b82f6', '&.Mui-checked': { color: '#3b82f6' } }}
                                                        />
                                                    }
                                                    label={<Typography sx={{ fontSize: '0.75em' }}>Face Tilt</Typography>}
                                                />
                                            </FormGroup>
                                        </Box>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <LineChart
                                                data={prepareChartData(sessionData.timeSeriesData)}
                                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                                <XAxis
                                                    dataKey="timestamp"
                                                    tickFormatter={formatTimestamp}
                                                    stroke="#718096"
                                                    fontSize={11}
                                                />
                                                <YAxis stroke="#718096" fontSize={11} />
                                                <Tooltip
                                                    labelFormatter={formatTimestamp}
                                                    contentStyle={{
                                                        backgroundColor: 'white',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: '0.375rem',
                                                        fontSize: '0.75em'
                                                    }}
                                                />
                                                <Legend 
                                                    wrapperStyle={{
                                                        fontSize: '0.75em',
                                                        color: '#4a5568'
                                                    }}
                                                />
                                                {visibleMetrics.faceTilt && (
                                                    <Line
                                                        type="monotone"
                                                        dataKey="faceTilt"
                                                        stroke="#3b82f6"
                                                        name="Face Tilt"
                                                        strokeWidth={2}
                                                    />
                                                )}
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </Paper>
                                </Grid>
                            </Grid>
                        )}

                        {activeTab === 2 && (
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 3, borderRadius: '0.75rem', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                                        <Typography variant="h6" sx={{ fontSize: '0.9em', fontWeight: 600, color: '#2d3748', mb: 3 }}>
                                            Session Statistics
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.82em', color: '#4a5568', mb: 1 }}>
                                            Total Duration: {sessionData.metadata?.duration.toFixed(2)}s
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.82em', color: '#4a5568', mb: 1 }}>
                                            Total Frames: {sessionData.metadata?.totalFrames || 0}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.82em', color: '#4a5568', mb: 1 }}>
                                            Frames Analyzed: {sessionData.metadata?.frames_analyzed || 0}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.82em', color: '#4a5568', mb: 1 }}>
                                            Face Detection: {sessionData.metadata?.viewer_detection?.stable_face_found ? 'Stable' : 'Unstable'}
                                            {sessionData.metadata?.viewer_detection?.face_stability_score ? 
                                                ` (${(sessionData.metadata.viewer_detection.face_stability_score * 100).toFixed(1)}%)` : ''}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.82em', color: '#4a5568', mb: 1 }}>
                                            Total Interactions: {sessionData.metadata?.interactions?.length || 0}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.82em', color: '#4a5568', mb: 1 }}>
                                            User: {sessionData.metadata?.user?.name || sessionData.metadata?.userName || 'Unknown'}
                                        </Typography>
                                    </Paper>
                                </Grid>

                                {sessionData.summaries && (
                                    <Grid item xs={12}>
                                        <Paper sx={{ p: 3, borderRadius: '0.75rem', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                                            <Typography variant="h6" sx={{ fontSize: '0.9em', fontWeight: 600, color: '#2d3748', mb: 3 }}>
                                                AI Analysis
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85em', fontWeight: 600, color: '#2d3748', mb: 2 }}>
                                                Concise Summary
                                            </Typography>
                                            <Box className="tooltip-content" sx={{ mb: 4 }}>
                                                {sessionData.summaries.concise?.split(/(?=#\s|##\s|\-\s)/gm).map((line, index) => {
                                                    const trimmedLine = line.trim();
                                                    if (trimmedLine.startsWith('# ')) {
                                                        return <Typography key={index} className="tooltip-h1" sx={{ fontSize: '0.82em', mb: 2, fontWeight: 600, color: '#2d3748' }}>{trimmedLine.substring(2).toUpperCase()}</Typography>;
                                                    } else if (trimmedLine.startsWith('## ')) {
                                                        return <Typography key={index} className="tooltip-h2" sx={{ fontSize: '0.78em', mb: 1.5, mt: 2, fontWeight: 600, color: '#2d3748' }}>{trimmedLine.substring(3).toUpperCase()}</Typography>;
                                                    } else if (trimmedLine.startsWith('- ')) {
                                                        return <Typography key={index} className="tooltip-paragraph" sx={{ fontSize: '0.75em', mb: 1, color: '#4a5568', lineHeight: 1.5 }}>{trimmedLine.substring(2)}</Typography>;
                                                    }
                                                    return null;
                                                })}
                                            </Box>
                                            <Typography sx={{ fontSize: '0.85em', fontWeight: 600, color: '#2d3748', mb: 2 }}>
                                                Standard Summary
                                            </Typography>
                                            <Box className="tooltip-content">
                                                {sessionData.summaries.standard?.split(/(?=#\s|##\s|\-\s)/gm).map((line, index) => {
                                                    const trimmedLine = line.trim();
                                                    if (trimmedLine.startsWith('# ')) {
                                                        return <Typography key={index} className="tooltip-h1" sx={{ fontSize: '0.82em', mb: 2, fontWeight: 600, color: '#2d3748' }}>{trimmedLine.substring(2).toUpperCase()}</Typography>;
                                                    } else if (trimmedLine.startsWith('## ')) {
                                                        return <Typography key={index} className="tooltip-h2" sx={{ fontSize: '0.78em', mb: 1.5, mt: 2, fontWeight: 600, color: '#2d3748' }}>{trimmedLine.substring(3).toUpperCase()}</Typography>;
                                                    } else if (trimmedLine.startsWith('- ')) {
                                                        return <Typography key={index} className="tooltip-paragraph" sx={{ fontSize: '0.75em', mb: 1, color: '#4a5568', lineHeight: 1.5 }}>{trimmedLine.substring(2)}</Typography>;
                                                    }
                                                    return null;
                                                })}
                                            </Box>
                                        </Paper>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </>
                )}
            </Box>
        </Modal>
    );
};

export default SessionDetailsModal;